import React, { lazy, Suspense, useEffect, useState } from "react";
import { clearSessionToken, getSessionToken } from "@/services/sessionToken";
import MainApp from "./components/MainApp";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import SkeletonLoader from "./components/FpPages/SkeletonLoader";
import { authRouteModules } from "./authRouteModules";
import { Button } from "@mui/material";
// The icon's own module, not the package barrel: the barrel re-exports
// every MUI icon, and under the unit project (where node_modules are not
// pre-bundled) importing it cost AuthWall.test.tsx about 20s.
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuthSignal, useCurrentUser } from "./hooks/useCurrentUser";
import EmbedAuthComplete from "./components/Embed/EmbedAuthComplete";
import EmbedSignInPrompt from "./components/Embed/EmbedSignInPrompt";
import EmbedFrame from "./components/Embed/EmbedFrame";
import AppShellSignInRequired from "./components/Embed/AppShellSignInRequired";
import AppShellUnavailable from "./components/Embed/AppShellUnavailable";
import { useEmbedMode } from "./components/Embed/EmbedContext";
import { isSecureLinkPath } from "./components/SecureLink/secureLinkPath";
import { releaseSsoAutoStart } from "./utils/ssoAutoStart";
import {
  CLIENT_ENVIRONMENT_ENTER_PATH,
  CLIENT_ENVIRONMENT_OPEN_PATH,
  isClientEnvironment,
  isStaffConsolePath,
  STAFF_CONSOLE_PATH,
} from "./services/clientEnvironment";
import ClientEnvironmentEnter from "./components/ClientEnvironment/ClientEnvironmentEnter";
import ClientEnvironmentOpen from "./components/ClientEnvironment/ClientEnvironmentOpen";
import ClientEnvironmentEnded from "./components/ClientEnvironment/ClientEnvironmentEnded";

// The sign-in pages load on demand (#12239): a signed-in visit never renders
// them, and they carry the Stytch client.
const LogOut = lazy(authRouteModules.LogOut);
const StytchTokenLogin = lazy(authRouteModules.StytchTokenLogin);
const LogInB2C = lazy(authRouteModules.LogInB2C);
const SecureLinkPage = lazy(authRouteModules.SecureLinkPage);
const OAuthAuthorize = lazy(authRouteModules.OAuthAuthorize);
const OAuthAuthorizeB2B = lazy(authRouteModules.OAuthAuthorizeB2B);
const OAuthPick = lazy(authRouteModules.OAuthPick);
const McpConnectorComplete = lazy(authRouteModules.McpConnectorComplete);
const ResetB2C = lazy(authRouteModules.ResetB2C);
const VerifyEmail = lazy(authRouteModules.VerifyEmail);
const OneNoteCallBackPage = lazy(authRouteModules.OneNoteCallBackPage);

// The placeholder while a sign-in page loads: the plain white those pages sit
// on, so the dark app skeleton never flashes in front of a login screen.
const WhiteFallback = () => <div className="fixed inset-0 bg-white" />;

// What a staff account whose own row is misplaced gets (#10718 slice 1b, M3):
// the staff console, and the warning everywhere else. Under a catch-all
// route, so the console's own routes match the whole address.
const StaffConsoleOrWarning: React.FC<{ warning: React.ReactNode }> = ({
  warning,
}) => {
  const { pathname } = useLocation();
  return isStaffConsolePath(pathname) ? <MainApp /> : <>{warning}</>;
};

const AuthWall = () => {
  useAuthSignal();
  const [stytchTokenUrl, setStytchTokenUrl] = useState<string | null>(null);
  const hasCookie = !!getSessionToken();
  const embedMode = useEmbedMode();
  const embedded = embedMode !== null;
  const clientEnvironment = isClientEnvironment();
  const onEntryPage =
    window.location.pathname.replace(/\/+$/, "") ===
    CLIENT_ENVIRONMENT_ENTER_PATH;
  const {
    data: userData,
    isBootstrapping,
    error,
    refetch: refetchCurrentUser,
  } = useCurrentUser();

  // When there's no session cookie, capture the intended destination so the
  // login flow can redirect back after sign-in. When the cookie appears (smooth
  // post-auth re-render), drop any captured token URL so we leave the token
  // resolver and render the authenticated tree.
  useEffect(() => {
    if (hasCookie) {
      setStytchTokenUrl(null);
      return;
    }
    const url = new URL(window.location.href);
    const stytchToken = url.searchParams.get("token");
    if (stytchToken) {
      setStytchTokenUrl(window.location.href);
      return;
    }
    const currentPath = window.location.pathname + window.location.search;
    if (
      currentPath !== "/" &&
      !isSecureLinkPath() &&
      !currentPath.includes("/login") &&
      !currentPath.includes("/logout")
    ) {
      sessionStorage.setItem("redirectAfterLogin", currentPath);
    }
  }, [hasCookie]);

  // A signed-in user has loaded, so the sign-in completed: an SSO-only
  // tenant's login page may start SSO on its own again the next time this tab
  // needs a sign-in. Released here, not when the cookie appears, so a session
  // the backend refuses cannot loop (utils/ssoAutoStart.ts).
  useEffect(() => {
    if (userData) releaseSsoAutoStart();
  }, [userData]);

  const GlobalWarning: React.FC<{
    requiresSso: boolean;
    misplaced?: boolean;
    /** The account keeps the staff console: the warning is the way to it. */
    keepsConsole?: boolean;
  }> = ({ requiresSso, misplaced = false, keepsConsole = false }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
      <div className="rounded-md border border-gray-200 p-16 text-gray-600 shadow-md">
        <div className="text-lg font-bold">
          {requiresSso
            ? "SSO REQUIRED"
            : misplaced
              ? "ACCOUNT IN A CLIENT ORGANIZATION"
              : "NO ORGANIZATION FOUND"}
        </div>
        <div className="mt-4 text-sm">
          {requiresSso ? (
            <>
              Your organization requires Single Sign-On (SSO) authentication.
              <br />
              Please log out and log in again using the SSO option.
            </>
          ) : misplaced ? (
            <>
              Finpilot staff accounts work from Finpilot&apos;s own
              organization, and yours is still in a client&apos;s.
              <br />
              Ask engineering to move it home.
            </>
          ) : (
            <>
              You are not currently in an organization.
              <br />
              Please contact your administrator.
            </>
          )}
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {keepsConsole && (
            <Button
              variant="outlined"
              href={STAFF_CONSOLE_PATH}
              sx={{ textTransform: "none" }}
            >
              Staff&nbsp;console
            </Button>
          )}
          <Button
            variant="outlined"
            href="/logout"
            sx={{ textTransform: "none" }}
          >
            <LogoutIcon sx={{ fontSize: 16, mr: 1 }} />
            Log&nbsp;Out
          </Button>
        </div>
      </div>
    </div>
  );

  // The client environment's entry page (#10718) needs no credential: it
  // trades its one-time code for the grant's token and turns this tab into
  // the client, whatever the tab was before.
  if (onEntryPage) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<ClientEnvironmentEnter />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // A secure credential link (#12505) is the same page for everyone, signed in
  // or not: the token in the URL is the credential, so it renders before any
  // of the cookie branches below, a client tab's included.
  if (isSecureLinkPath()) {
    return (
      <div className="w-full h-full">
        <BrowserRouter>
          <Suspense fallback={<WhiteFallback />}>
            <Routes>
              <Route path="/s/:token" element={<SecureLinkPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    );
  }

  // Inside the Finpilot Notetaker app (#9163) the app signs in natively and
  // hands the page its session before it loads. With none, the page tells the
  // app, which signs out and shows its own sign-in; there is no page sign-in
  // here, not even the frame's popup, which would open a browser window the
  // app cannot see.
  if (embedMode === "app" && !hasCookie) {
    return (
      <div className="w-full h-full">
        <AppShellSignInRequired />
      </div>
    );
  }

  // A client tab has no sign-in of its own: its only credential is the
  // grant's token. Without a live one (the grant ended and identity was
  // refused, or another tab ended it) it says that access ended.
  if (clientEnvironment && (!hasCookie || (!userData && error))) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<ClientEnvironmentEnded />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // An embedded frame gets its own sign-in gate: a popup handshake, not a login
  // page. A cross-site frame cannot read the session cookie and cannot run SSO
  // in place, so the ordinary login screen would be a dead end here. Checked
  // before the cookie branch below, and on any route, because embed mode is a
  // property of the document rather than of one path (#11021).
  if (embedded && !hasCookie) {
    return (
      <div className="w-full h-full">
        <BrowserRouter>
          <Routes>
            {/* Still reachable so the popup can land and close itself even if
                the opener navigated meanwhile. */}
            <Route
              path="/embed/auth/complete"
              element={<EmbedAuthComplete />}
            />
            <Route path="*" element={<EmbedSignInPrompt />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  if (stytchTokenUrl) {
    return (
      <div className="w-full h-full bg-white">
        <BrowserRouter>
          <Suspense fallback={<WhiteFallback />}>
            <Routes>
              <Route path="/reset" element={<ResetB2C />} />
              <Route
                path="*"
                element={<StytchTokenLogin stytchTokenUrl={stytchTokenUrl} />}
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    );
  }

  // No cookie → login screen (or auth-related public routes).
  if (!hasCookie) {
    return (
      <div className="w-full h-full">
        <BrowserRouter>
          <Suspense fallback={<WhiteFallback />}>
            <Routes>
              <Route
                path="onenote/callback"
                element={<OneNoteCallBackPage />}
              />
              <Route path="/logout" element={<LogOut userData={userData} />} />
              <Route path="/reset" element={<ResetB2C />} />
              <Route
                path="/__admin/login"
                element={<LogInB2C variant="staff" />}
              />
              <Route path="*" element={<LogInB2C />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    );
  }

  // Cookie present but no user data yet and no cached identity — first load
  // after a fresh sign-in. If the login flow stashed an email hint, render
  // VerifyEmail directly so password sign-ups land straight on the OTP page
  // (matching the white background of the login page) instead of flashing the
  // dark MainApp skeleton. Otherwise show the skeleton while fp0052 is in
  // flight.
  //
  // Invite flow is the exception: an `?invite=` in the URL means the user is
  // joining via an emailed invite, which already verifies their email
  // server-side. They never do the OTP step, so don't flash the verification
  // page during bootstrap (a stale hint from an earlier attempt would
  // otherwise trip it) — show the skeleton and let fp0052 land them in the app.
  if (!userData && isBootstrapping) {
    const emailHint = sessionStorage.getItem("verify_email_hint");
    const inInviteFlow = new URLSearchParams(window.location.search).has(
      "invite"
    );
    if (emailHint && !inInviteFlow) {
      return (
        <BrowserRouter>
          <Suspense fallback={<WhiteFallback />}>
            <Routes>
              <Route path="/logout" element={<LogOut userData={null} />} />
              <Route
                path="*"
                element={<VerifyEmail userData={{ email: emailHint }} />}
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      );
    }
    return (
      <BrowserRouter>
        <SkeletonLoader />
      </BrowserRouter>
    );
  }

  // Cookie present, fetch failed, and nothing cached — cookie is bad. Bounce
  // to login (LogOut here would revoke the session; LogInB2C lets the user
  // re-auth without nuking anything).
  //
  // Embedded, the login page is a dead end — it cannot run SSO in a frame — so
  // a rejected session sends the viewer back to the popup handshake instead.
  // The stale in-memory token is dropped first, otherwise the prompt would sit
  // behind a session the app still believes it has (#11021).
  if (!userData && error) {
    if (embedMode === "app") {
      // A rejected session never reaches here: the sign-in read drops a
      // handed-over token on a 401, and the no-session branch above tells
      // the app. What is left is the server or the network, which must not
      // sign the app out (#9163).
      return (
        <div className="w-full h-full">
          <AppShellUnavailable onRetry={refetchCurrentUser} />
        </div>
      );
    }
    if (embedded) {
      clearSessionToken();
      return (
        <div className="w-full h-full">
          <BrowserRouter>
            <Routes>
              <Route
                path="/embed/auth/complete"
                element={<EmbedAuthComplete />}
              />
              <Route path="*" element={<EmbedSignInPrompt />} />
            </Routes>
          </BrowserRouter>
        </div>
      );
    }
    return (
      <div className="w-full h-full">
        <BrowserRouter>
          <Suspense fallback={<WhiteFallback />}>
            <Routes>
              <Route path="/logout" element={<LogOut userData={null} />} />
              <Route path="/reset" element={<ResetB2C />} />
              <Route
                path="/__admin/login"
                element={<LogInB2C variant="staff" />}
              />
              <Route path="*" element={<LogInB2C />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    );
  }

  // From here, userData may be cached (stale) or fresh. Either way it's
  // correct enough to render with — the background refresh will dispatch
  // updates if anything changed.
  if (userData && !userData.email_verified) {
    return (
      <BrowserRouter>
        <Suspense fallback={<WhiteFallback />}>
          <Routes>
            <Route path="/logout" element={<LogOut userData={userData} />} />
            <Route path="*" element={<VerifyEmail userData={userData} />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  const needsSso = userData?.requiresSsoLogin === true;
  const noOrg = !!userData && !userData.organization;
  if (userData && (needsSso || noOrg)) {
    const misplaced = userData.homeOrganizationMisplaced === true;
    // A staff account whose own row still sits in a client organization has
    // no organization of its own until it is moved home, and keeps the staff
    // console so it can still open grants (#10718 slice 1b, M3): the console
    // and the page that opens a client environment, nothing else.
    const keepsConsole =
      misplaced && !needsSso && !clientEnvironment && userData.isStaff === true;
    const warning = (
      <GlobalWarning
        requiresSso={needsSso}
        misplaced={misplaced}
        keepsConsole={keepsConsole}
      />
    );
    return (
      <BrowserRouter>
        {/* The warning is also what shows while the logout page loads. */}
        <Suspense fallback={warning}>
          <Routes>
            <Route
              path="/logout"
              element={
                <>
                  {warning}
                  <LogOut userData={userData} />
                </>
              }
            />
            {keepsConsole && (
              <Route
                path={CLIENT_ENVIRONMENT_OPEN_PATH}
                element={<ClientEnvironmentOpen />}
              />
            )}
            <Route
              path="*"
              element={
                keepsConsole ? (
                  <StaffConsoleOrWarning warning={warning} />
                ) : (
                  warning
                )
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<SkeletonLoader />}>
        <Routes>
          <Route path="/logout" element={<LogOut userData={userData} />} />
          <Route path="/oauth/pick" element={<OAuthPick />} />
          {/* Where the remote-MCP callback lands the popped tab so it can close
            itself (#10595). Routed here, above the AuthWall's gate, because the
            tab comes back from a third-party redirect and shouldn't be bounced
            through a login just to close. */}
          <Route
            path="/oauth/mcp/complete"
            element={<McpConnectorComplete />}
          />
          {/* Where the embed's sign-in popup lands once authenticated, so it can
            hand the session to the frame that opened it and close (#11021). */}
          <Route path="/embed/auth/complete" element={<EmbedAuthComplete />} />
          <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
          <Route path="/oauth/authorize-b2b" element={<OAuthAuthorizeB2B />} />
          {/* An authenticated user landing on the login/invite link is a no-op:
            send them home instead of falling through to MainApp's 404. This
            also covers the just-registered user whose email becomes verified
            while the URL is still /login. */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          {/* Open the client named by `?org=` and send this tab into it
            (#10718). Always a home page, so always mounted here: the ended
            page's Reopen and the account menu's Admin access land on it. */}
          <Route
            path={CLIENT_ENVIRONMENT_OPEN_PATH}
            element={<ClientEnvironmentOpen />}
          />
          <Route
            path="*"
            element={
              // The "Open in Finpilot" way out is the frame's alone: the app
              // is Finpilot (#9163).
              embedMode === "frame" ? (
                <EmbedFrame>
                  <MainApp />
                </EmbedFrame>
              ) : (
                <MainApp />
              )
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AuthWall;
